/**
 * CrewLocationLayer — Shows all active crew positions on the map (admin/inhouse view).
 * Polls /api/worker-locations every 30 seconds.
 * Renders inside <GoogleMap> as a child component.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Marker, InfoWindow } from '@react-google-maps/api';
import { loadCrewLocations } from '../../services/mapService';

// Distinct colors for crew members
const CREW_COLORS = [
  '#E91E63', '#9C27B0', '#673AB7', '#3F51B5', '#009688',
  '#FF5722', '#795548', '#FF9800', '#00BCD4', '#8BC34A',
];

function getCrewColor(index) {
  return CREW_COLORS[index % CREW_COLORS.length];
}

function buildCrewSvg(color, initial) {
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='32' height='32'>
    <circle cx='16' cy='16' r='14' fill='${color}' stroke='white' stroke-width='2.5'/>
    <text x='16' y='21' text-anchor='middle' font-size='14' font-weight='700' font-family='Arial' fill='white'>${initial}</text>
  </svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

function timeAgo(dateStr) {
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now - d;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  return `${hrs}h ago`;
}

function formatSpeed(speed) {
  if (speed == null || speed <= 0) return 'Stationary';
  const mph = speed * 2.237; // m/s to mph
  return `${mph.toFixed(1)} mph`;
}

export default function CrewLocationLayer({ projectId, darkMode, loggedInUser }) {
  const [crewLocations, setCrewLocations] = useState([]);
  const [selectedCrew, setSelectedCrew] = useState(null);
  const pollRef = useRef(null);

  const fetchLocations = useCallback(async () => {
    try {
      const locs = await loadCrewLocations(projectId);
      // Filter out the current user's own location (shown by MyLocationMarker)
      const others = (locs || []).filter(l => l.worker_email !== loggedInUser?.email);
      setCrewLocations(others);
    } catch (err) {
      console.warn('[CrewLocationLayer] fetch failed:', err);
    }
  }, [projectId, loggedInUser]);

  useEffect(() => {
    fetchLocations();
    pollRef.current = setInterval(fetchLocations, 30000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [fetchLocations]);

  if (!crewLocations.length || !window.google) return null;

  return (
    <>
      {crewLocations.map((crew, idx) => {
        const lat = parseFloat(crew.lat);
        const lng = parseFloat(crew.lng);
        if (isNaN(lat) || isNaN(lng)) return null;

        const email = crew.worker_email || '';
        const initial = email.charAt(0).toUpperCase();
        const color = getCrewColor(idx);
        const name = email.split('@')[0].replace(/[._]/g, ' ');

        return (
          <React.Fragment key={email}>
            <Marker
              position={{ lat, lng }}
              title={name}
              icon={{
                url: buildCrewSvg(color, initial),
                scaledSize: new window.google.maps.Size(32, 32),
                anchor: new window.google.maps.Point(16, 16),
              }}
              onClick={() => setSelectedCrew(crew)}
              zIndex={1200}
            />
          </React.Fragment>
        );
      })}

      {/* Crew detail popup */}
      {selectedCrew && (
        <InfoWindow
          position={{ lat: parseFloat(selectedCrew.lat), lng: parseFloat(selectedCrew.lng) }}
          onCloseClick={() => setSelectedCrew(null)}
          options={{ maxWidth: 250 }}
        >
          <div style={{ fontFamily: 'system-ui, sans-serif', fontSize: 13, color: '#333', minWidth: 180 }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>
              {(selectedCrew.worker_email || '').split('@')[0].replace(/[._]/g, ' ')}
            </div>
            <div style={{ fontSize: 11, color: '#666', marginBottom: 6 }}>
              {selectedCrew.worker_email}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '2px 8px', fontSize: 12 }}>
              <span style={{ color: '#888' }}>Last seen:</span>
              <span>{selectedCrew.timestamp ? timeAgo(selectedCrew.timestamp) : 'Unknown'}</span>
              <span style={{ color: '#888' }}>Speed:</span>
              <span>{formatSpeed(selectedCrew.speed)}</span>
              {selectedCrew.activity && (
                <>
                  <span style={{ color: '#888' }}>Activity:</span>
                  <span>{selectedCrew.activity}</span>
                </>
              )}
              {selectedCrew.nearest_segment_id && (
                <>
                  <span style={{ color: '#888' }}>Near:</span>
                  <span>{selectedCrew.nearest_segment_id} ({selectedCrew.nearest_segment_distance || '?'}m)</span>
                </>
              )}
              {selectedCrew.battery_level != null && (
                <>
                  <span style={{ color: '#888' }}>Battery:</span>
                  <span>{selectedCrew.battery_level}%</span>
                </>
              )}
            </div>
          </div>
        </InfoWindow>
      )}
    </>
  );
}
